import { useState, useEffect, useRef } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Custom hook: browser native speech-to-text (Web Speech API). Free, no API costs.
export const useSpeechRecognition = (onResult) => {
  const [isListening, setIsListening] = useState(false);
  const [supported, setSupported] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setSupported(false);
      return;
    }
    setSupported(true);
    const rec = new SpeechRecognition();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "en-US";
    rec.onresult = (e) => {
      let finalText = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) {
          finalText += e.results[i][0].transcript + " ";
        }
      }
      if (finalText) onResult(finalText.trim());
    };
    rec.onerror = () => setIsListening(false);
    rec.onend = () => setIsListening(false);
    recognitionRef.current = rec;
  }, [onResult]);

  const start = () => {
    if (!recognitionRef.current || isListening) return;
    try {
      recognitionRef.current.start();
      setIsListening(true);
    } catch {}
  };
  const stop = () => {
    if (!recognitionRef.current) return;
    try {
      recognitionRef.current.stop();
    } catch {}
    setIsListening(false);
  };

  return { isListening, supported, start, stop };
};

// ===== Voice Input Box (textarea + mic toggle) =====
export const VoiceInputBox = ({ value, onChange, placeholder = "Type or tap 🎤 to dictate...", rows = 3, testIdPrefix = "voice-input" }) => {
  const appendText = (txt) => {
    onChange((prev) => (prev ? `${prev} ${txt}` : txt));
  };
  const { isListening, supported, start, stop } = useSpeechRecognition(appendText);
  return (
    <div style={{ position: "relative" }}>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        data-testid={`${testIdPrefix}-textarea`}
        style={{
          width: "100%",
          padding: "10px 44px 10px 12px",
          borderRadius: 8,
          border: "1px solid rgba(255,255,255,0.12)",
          background: "rgba(255,255,255,0.04)",
          color: "#fff",
          fontSize: 14,
          fontFamily: "inherit",
          resize: "vertical",
          boxSizing: "border-box",
        }}
      />
      {supported && (
        <button
          type="button"
          onClick={isListening ? stop : start}
          data-testid={`${testIdPrefix}-mic`}
          title={isListening ? "Stop dictation" : "Start dictation"}
          style={{
            position: "absolute",
            right: 8,
            top: 8,
            width: 32,
            height: 32,
            borderRadius: "50%",
            border: "none",
            background: isListening ? "#FF4D6D" : "rgba(0,255,255,0.18)",
            color: isListening ? "#fff" : "#00FFFF",
            cursor: "pointer",
            fontSize: 14,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            animation: isListening ? "mfPulse 1.2s infinite" : "none",
          }}
        >
          {isListening ? "■" : "🎤"}
        </button>
      )}
    </div>
  );
};

// ===== Trigger Thread (admin view inside dashboard) =====
export const TriggerThread = ({ triggerId, onClose, isCEO = true, isCOO = false }) => {
  const [trigger, setTrigger] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [callbackOpen, setCallbackOpen] = useState(false);
  const [callbackForm, setCallbackForm] = useState({ due_date: "", note: "" });
  const [schedulingCallback, setSchedulingCallback] = useState(false);

  const load = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/triggers/${triggerId}`, { credentials: "include" });
      if (r.ok) setTrigger(await r.json());
    } catch {}
    setLoading(false);
  };
  useEffect(() => {
    load();
    const interval = setInterval(load, 45000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerId]);

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/triggers/${triggerId}/reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ text: reply }),
      });
      if (r.ok) {
        setTrigger(await r.json());
        setReply("");
      }
    } catch {}
    setSending(false);
  };

  const setStatus = async (status) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/triggers/${triggerId}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status }),
      });
      if (r.ok) setTrigger(await r.json());
    } catch {}
  };

  const scheduleCallback = async () => {
    if (!callbackForm.due_date) return;
    setSchedulingCallback(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/triggers/${triggerId}/schedule-callback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(callbackForm),
      });
      if (r.ok) {
        setCallbackOpen(false);
        setCallbackForm({ due_date: "", note: "" });
        load();
      }
    } catch {}
    setSchedulingCallback(false);
  };

  if (loading) return <div style={{ padding: 30, color: "#7E92A8" }}>Loading thread…</div>;
  if (!trigger) return <div style={{ padding: 30, color: "#FF4D6D" }}>Trigger not found.</div>;

  const statusMeta = {
    open: { color: "#FF4D6D", label: "🔴 OPEN", next: "responding", nextLabel: "Mark Responding" },
    responding: { color: "#FFB347", label: "🟡 RESPONDING", next: "resolved", nextLabel: "Mark Resolved" },
    resolved: { color: "#00DC78", label: "🟢 RESOLVED", next: null, nextLabel: null },
  };
  const sm = statusMeta[trigger.status] || statusMeta.open;

  return (
    <div data-testid="trigger-thread" style={{ background: "#0A1A2E", borderRadius: 14, padding: 22, maxWidth: 720, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, color: "#00FFFF", letterSpacing: "0.12em", fontWeight: 800, marginBottom: 4 }}>FIELD TRIGGER</div>
          <h3 style={{ color: "#fff", margin: 0, fontSize: 18 }}>{trigger.address}</h3>
          <div style={{ marginTop: 6, display: "inline-flex", gap: 8, alignItems: "center" }}>
            <span style={{ padding: "3px 9px", borderRadius: 4, background: `${sm.color}22`, color: sm.color, fontSize: 11, fontWeight: 800 }}>{sm.label}</span>
            {trigger.owner_acknowledged ? (
              <span style={{ padding: "3px 9px", borderRadius: 4, background: "rgba(0,220,120,0.18)", color: "#00DC78", fontSize: 11, fontWeight: 700 }}>👁 Owner viewed</span>
            ) : (
              <span style={{ padding: "3px 9px", borderRadius: 4, background: "rgba(255,179,71,0.18)", color: "#FFB347", fontSize: 11, fontWeight: 700 }}>⏳ Owner not yet viewed</span>
            )}
          </div>
        </div>
        {onClose && <button onClick={onClose} data-testid="trigger-close" style={{ background: "transparent", border: "none", color: "#7E92A8", fontSize: 22, cursor: "pointer" }}>✕</button>}
      </div>

      <div style={{ marginBottom: 14 }}>
        <img
          src={`${BACKEND_URL}/api/files/${trigger.storage_path}`}
          alt="Trigger evidence"
          style={{ width: "100%", maxHeight: 320, objectFit: "contain", borderRadius: 8, background: "#000" }}
        />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16, maxHeight: 280, overflowY: "auto", padding: "0 4px" }} data-testid="trigger-messages">
        {(trigger.messages || []).map((m) => {
          const roleMeta = {
            crew: { color: "#FFB347", label: "Crew" },
            ceo: { color: "#00FFFF", label: "Shannon (CEO)" },
            coo: { color: "#A78BFA", label: "Shaneka (COO)" },
            admin: { color: "#5AC8D8", label: "Admin" },
            owner: { color: "#00DC78", label: "Property Owner" },
          };
          const rm = roleMeta[m.author_role] || { color: "#ADBED1", label: m.author_role };
          return (
            <div key={m.msg_id} style={{ padding: 10, borderRadius: 8, background: `${rm.color}0F`, borderLeft: `3px solid ${rm.color}` }}>
              <div style={{ fontSize: 10, color: rm.color, fontWeight: 800, letterSpacing: "0.06em", marginBottom: 4 }}>
                {rm.label.toUpperCase()} · {m.author_name} · {new Date(m.created_at).toLocaleString()}
              </div>
              <div style={{ color: "#fff", fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{m.text}</div>
            </div>
          );
        })}
      </div>

      {trigger.status !== "resolved" && (
        <>
          {trigger.callback_scheduled && (
            <div style={{ marginBottom: 10, padding: 10, borderRadius: 8, background: "rgba(0,220,120,0.10)", border: "1px solid rgba(0,220,120,0.30)", fontSize: 12, color: "#00DC78" }}>
              📞 Callback scheduled for <strong>{trigger.callback_due_date}</strong> — task on your dashboard.
            </div>
          )}
          <VoiceInputBox value={reply} onChange={setReply} placeholder="Type or tap 🎤 to dictate your reply..." testIdPrefix="trigger-reply" />
          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <button
              onClick={sendReply}
              disabled={!reply.trim() || sending}
              data-testid="trigger-send"
              style={{ flex: "2 1 200px", padding: "10px 14px", borderRadius: 8, border: "1px solid #00FFFF", background: "rgba(0,255,255,0.18)", color: "#00FFFF", fontSize: 13, fontWeight: 800, cursor: sending || !reply.trim() ? "not-allowed" : "pointer", opacity: sending || !reply.trim() ? 0.5 : 1 }}
            >
              {sending ? "Sending…" : "Send Reply"}
            </button>
            {!trigger.callback_scheduled && (
              <button
                onClick={() => setCallbackOpen(true)}
                data-testid="trigger-schedule-callback"
                style={{ flex: "1 1 140px", padding: "10px 14px", borderRadius: 8, border: "1px solid #00DC78", background: "transparent", color: "#00DC78", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
              >
                📞 Schedule Callback
              </button>
            )}
            {sm.next && (
              <button
                onClick={() => setStatus(sm.next)}
                data-testid={`trigger-status-${sm.next}`}
                style={{ flex: "1 1 140px", padding: "10px 14px", borderRadius: 8, border: `1px solid ${sm.color}`, background: "transparent", color: sm.color, fontSize: 12, fontWeight: 700, cursor: "pointer" }}
              >
                {sm.nextLabel}
              </button>
            )}
          </div>
          {/* Callback scheduling sub-modal */}
          {callbackOpen && (
            <div data-testid="callback-modal" style={{ position: "fixed", inset: 0, background: "rgba(3,11,26,0.92)", backdropFilter: "blur(10px)", zIndex: 1700, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
              <div style={{ background: "#0A1A2E", border: "1px solid rgba(0,220,120,0.30)", borderRadius: 12, padding: 22, maxWidth: 440, width: "100%" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                  <h3 style={{ color: "#fff", margin: 0, fontSize: 17 }}>📞 Schedule Callback</h3>
                  <button onClick={() => setCallbackOpen(false)} style={{ background: "transparent", border: "none", color: "#7E92A8", fontSize: 20, cursor: "pointer" }}>✕</button>
                </div>
                <p style={{ color: "#ADBED1", fontSize: 12, margin: "0 0 14px" }}>Creates a high-priority task on your dashboard so you don't drop the ball.</p>
                <input type="date" value={callbackForm.due_date} onChange={(e) => setCallbackForm(p => ({ ...p, due_date: e.target.value }))} data-testid="callback-due-date" style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: "#fff", fontSize: 13, marginBottom: 8, boxSizing: "border-box" }} />
                <textarea value={callbackForm.note} onChange={(e) => setCallbackForm(p => ({ ...p, note: e.target.value }))} placeholder="What's the callback about? (optional)" rows={3} data-testid="callback-note" style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: "#fff", fontSize: 13, fontFamily: "inherit", resize: "vertical", marginBottom: 14, boxSizing: "border-box" }} />
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => setCallbackOpen(false)} style={{ flex: 1, padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.10)", background: "transparent", color: "#ADBED1", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Cancel</button>
                  <button onClick={scheduleCallback} disabled={schedulingCallback || !callbackForm.due_date} data-testid="callback-confirm" style={{ flex: 2, padding: "10px 14px", borderRadius: 8, border: "1px solid #00DC78", background: "rgba(0,220,120,0.18)", color: "#00DC78", fontSize: 13, fontWeight: 800, cursor: (schedulingCallback || !callbackForm.due_date) ? "not-allowed" : "pointer", opacity: (schedulingCallback || !callbackForm.due_date) ? 0.5 : 1 }}>{schedulingCallback ? "Scheduling…" : "📞 Schedule"}</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      {trigger.status === "resolved" && (
        <div style={{ padding: 14, borderRadius: 8, background: "rgba(0,220,120,0.10)", color: "#00DC78", fontSize: 13, fontWeight: 700, textAlign: "center" }}>
          ✅ This trigger has been resolved. Thread is read-only.
        </div>
      )}
    </div>
  );
};

// ===== Active Triggers Widget (dashboard home) =====
export const ActiveTriggersWidget = ({ onOpenTrigger }) => {
  const [triggers, setTriggers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/triggers?status=active`, { credentials: "include" });
      if (r.ok) setTriggers(await r.json());
    } catch {}
    setLoading(false);
  };
  useEffect(() => {
    load();
    const interval = setInterval(load, 120000);
    return () => clearInterval(interval);
  }, []);

  if (loading || triggers.length === 0) return null;

  return (
    <div data-testid="active-triggers-widget" style={{ background: "linear-gradient(135deg, #1a0a1a 0%, #2a0a14 100%)", border: "1.5px solid rgba(255,77,109,0.4)", borderRadius: 14, padding: 18, marginBottom: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 10, color: "#FF4D6D", letterSpacing: "0.14em", fontWeight: 800, marginBottom: 4 }}>🔴 ACTIVE FIELD TRIGGERS</div>
          <h3 style={{ color: "#fff", margin: 0, fontSize: 17 }}>{triggers.length} issue{triggers.length === 1 ? "" : "s"} awaiting response</h3>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {triggers.slice(0, 5).map((t) => (
          <button
            key={t.trigger_id}
            onClick={() => onOpenTrigger(t.trigger_id)}
            data-testid={`active-trigger-${t.trigger_id}`}
            style={{ textAlign: "left", padding: "10px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.03)", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: "#fff", fontSize: 13, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.address}</div>
              <div style={{ color: "#ADBED1", fontSize: 11, marginTop: 2 }}>
                {t.messages?.length || 0} message{(t.messages?.length || 0) === 1 ? "" : "s"} · last: {new Date(t.last_activity_at).toLocaleString()}
              </div>
            </div>
            <span style={{ padding: "3px 8px", borderRadius: 4, fontSize: 10, fontWeight: 800, background: t.status === "open" ? "rgba(255,77,109,0.22)" : "rgba(255,179,71,0.22)", color: t.status === "open" ? "#FF4D6D" : "#FFB347" }}>
              {t.status === "open" ? "OPEN" : "RESPONDING"}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};
